import { Bitcoin, CreditCard, type LucideIcon } from 'lucide-react'
import { isPositiveCredits } from '@/features/ai/lib/money'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

/**
 * Пакет пополнения (§1.4, camelCase). Номинал и обе цены — СТРОКИ: реальный
 * ответ прода `{"credits":"50.0000","priceUsd":"5.00","priceRub":"500.00"}`
 * (ответ бэка от 14.08.2026, §0). `credits` — такие же деньги, как баланс, и
 * к `Number` не приводится (`features/ai/lib/money.ts`).
 */
export interface CreditPackage {
  id: number
  code: string
  name: string
  credits: string
  priceUsd: string
  priceRub: string | null
  sortOrder: number
}

/**
 * В веб-студии ровно два способа. Плеер показывает ещё `XTR`, но только внутри
 * Telegram-мини-аппа: без `telegramId` старт отобьётся до оплаты, и кнопка была
 * бы фальшивой affordance. `FOREIGN_CARD`/`BASE_ETH_USDT` не показывает и он.
 */
export type PaymentMethod = 'TBANK' | 'HELEKET'

export interface MethodOption {
  id: PaymentMethod
  label: string
  /** Готовый текст цены для подписи кнопки оплаты. */
  price: string
  icon: LucideIcon
}

/**
 * «Цена предложена» — предикат боевого плеера (`isPriceOffered`): не `null`,
 * не пустая строка и строго больше нуля. Проверка только на `!== null`
 * пропустила бы `"0"`/`""` в живой способ оплаты, который отобьётся в воронке.
 *
 * Сравнение с нулём делается по строке: у неотрицательной денежной строки
 * «больше нуля» это ровно «есть хоть одна ненулевая цифра». `Number` к деньгам
 * в этом репозитории не применяется (см. `features/ai/lib/money.ts`).
 *
 * Экспортирован намеренно: тот же вопрос («показывать ли рублёвую цену вообще»)
 * задаёт и карточка пакета (`PackageCard`) — один источник признака, не две
 * копии условия.
 */
/** Реализация живёт в `money.ts`: «цена предложена» и «заморозка есть» — один
 *  и тот же вопрос к денежной строке, и две копии регулярки разошлись бы. */
export const isPriceOffered = (price: string | null): boolean => isPositiveCredits(price)

export const usdText = (value: string): string => `$${value}`
export const rubText = (value: string): string => `${value} ₽`

/**
 * Правило региона (PRD #71, п.28), образец — веб `academy_frontend`
 * `src/lib/payments/getAvailableMethods.ts` (без `XTR`):
 * - `RU` — только `TBANK`, в рублях;
 * - другая страна — только `HELEKET`, в долларах;
 * - `null` (страна неизвестна) — `TBANK` и `HELEKET`, цена пакета в долларах.
 * `TBANK` по-прежнему требует почту, и рублёвая цена должна быть предложена
 * (`> 0`). Разрыв веба перенесён как есть: у `RU` без `priceRub` способов нет, и
 * долларовым запасным путём он не лечится. `FOREIGN_CARD` — после PRD #65.
 */
const cardAllowed = (country: string | null): boolean => country === 'RU' || country === null
const cryptoAllowed = (country: string | null): boolean => country !== 'RU'

/**
 * Цена пакета для карточки: ОДНА. У `RU` — рублёвая (если предложена), у
 * остальных и у неизвестной страны — долларовая. Предикат «цена предложена» тот
 * же, что у способов оплаты. Почта цену не прячет: у RU без неё карточка
 * остаётся выбираемой (`isSelectable`), иначе форма почты на шаге оплаты
 * недостижима.
 */
export const packagePrice = (pkg: CreditPackage, country: string | null): string | null => {
  if (country === 'RU') return isPriceOffered(pkg.priceRub) ? rubText(pkg.priceRub ?? '') : null
  return isPriceOffered(pkg.priceUsd) ? usdText(pkg.priceUsd) : null
}

/**
 * Способы оплаты выбранного пакета. Пустой массив — оплатить его нечем.
 *
 * `canPayByCard` — есть ли у автора почта в профиле: `TBANK` без неё отбивает
 * покупку кодом `email_required_for_tbank`, и бэк прямо советует отсекать это
 * ДО показа кнопки (ответ от 14.08.2026, §3). Аргумент обязательный: способ,
 * который гарантированно отобьётся, — фальшивая affordance. Страна — тоже
 * обязательная: `null` значит «неизвестна», а не «забыли передать».
 */
export const methodsForPackage = (
  pkg: CreditPackage,
  canPayByCard: boolean,
  country: string | null,
): MethodOption[] => {
  const out: MethodOption[] = []
  if (cardAllowed(country) && canPayByCard && isPriceOffered(pkg.priceRub))
    out.push({
      id: 'TBANK',
      label: messagesNow(messages).methodRussianCard,
      price: rubText(pkg.priceRub ?? ''),
      icon: CreditCard,
    })
  if (cryptoAllowed(country) && isPriceOffered(pkg.priceUsd))
    out.push({
      id: 'HELEKET',
      label: messagesNow(messages).methodCrypto,
      price: usdText(pkg.priceUsd),
      icon: Bitcoin,
    })
  return out
}

/**
 * Пакет можно оплатить хоть чем-то. Витрина обязана спрашивать это ДО того,
 * как позовёт автора нажать «Выбрать»: шаг «этот пакет сейчас нельзя оплатить»
 * — страховка на битые данные, а не место, куда ведут подсказкой.
 */
export const isPayable = (
  pkg: CreditPackage,
  canPayByCard: boolean,
  country: string | null,
): boolean => methodsForPackage(pkg, canPayByCard, country).length > 0

/**
 * Карточку можно выбрать. Сверх `isPayable`: RU без почты при предложенной
 * рублёвой цене — выбирать можно, шаг оплаты (`MethodPicker`) назовёт причину и
 * покажет `ContactEmailForm`; другого пути к форме у студии нет. У RU без
 * `priceRub` и у прочих тупиков остаётся «недоступно».
 */
export const isSelectable = (
  pkg: CreditPackage,
  canPayByCard: boolean,
  country: string | null,
): boolean =>
  isPayable(pkg, canPayByCard, country) ||
  (country === 'RU' && isCardHiddenForEmail(pkg, canPayByCard, country))

/**
 * Рублёвый способ спрятан не потому, что цены нет, а потому, что у автора нет
 * почты. Отдельный признак нужен обоим шагам: без него «нельзя оплатить»
 * звучало бы как поломка витрины, а причина лечится профилем. Для страны, где
 * карты нет по региону, почта не причина — признак ложный.
 */
export const isCardHiddenForEmail = (
  pkg: CreditPackage,
  canPayByCard: boolean,
  country: string | null,
): boolean => cardAllowed(country) && !canPayByCard && isPriceOffered(pkg.priceRub)
